(() => {
  'use strict';
  const body = document.body;
  if (!body.matches('.brand-retroengel[data-re-motion="v1"]') || body.dataset.reMotionReady) return;
  body.dataset.reMotionReady = 'true';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 900px)');
  const mobile = matchMedia('(max-width: 899px)');
  const ease = 'cubic-bezier(.2,.72,.25,1)';
  const mounted = new WeakSet(), seen = new WeakSet();
  const animations = new Map(), targets = new Set(), depth = new Map(), drift = new Set(), visibleDrift = new Set();
  const banners = new Map();
  let revealObserver, driftObserver, pointerFrame = 0, scrollFrame = 0, suspended = false;

  function animate(node, frames, options) {
    if (mobile.matches || reduced.matches || suspended || document.hidden || !node?.animate) return;
    animations.get(node)?.cancel();
    const animation = node.animate(frames, {duration:640, easing:ease, ...options});
    animations.set(node, animation);
    const finish = () => { if (animations.get(node) === animation) animations.delete(node); };
    animation.finished.then(finish, finish);
  }
  function show(node, delay = 0) {
    if (seen.has(node)) return;
    seen.add(node);
    node.dataset.reReveal = 'shown';
    revealObserver?.unobserve(node);
    animate(node, [{opacity:.08,transform:`translate3d(0,${mobile.matches?16:24}px,0)`},{opacity:1,transform:'translate3d(0,0,0)'}], {duration:mobile.matches?520:640,delay,fill:'backwards'});
  }
  function makeObservers() {
    if (!('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver(entries => {
      const entering = entries.filter(entry => entry.isIntersecting && !seen.has(entry.target));
      // The grid's natural document order sets a bounded left-to-right cadence.
      entering.forEach((entry, index) => show(entry.target, Math.min(index, 3) * (mobile.matches?45:65)));
    }, {threshold:.06,rootMargin:'0px 0px -32px 0px'});
    driftObserver = new IntersectionObserver(entries => {
      entries.forEach(({target,isIntersecting}) => {
        if (isIntersecting) visibleDrift.add(target); else {visibleDrift.delete(target);target.style.removeProperty('--re-drift-y');}
      });
      scheduleScroll();
    }, {threshold:0});
  }
  function reveal(node) {
    if (mounted.has(node) || node.closest('form,.ss-gallery,.ss-purchase,[data-cart-root],dialog,.re-banner')) return;
    mounted.add(node);targets.add(node);node.dataset.reReveal='ready';
    if (mobile.matches || reduced.matches || !revealObserver || node.getBoundingClientRect().bottom < 0) {
      seen.add(node);node.dataset.reReveal='shown';return;
    }
    revealObserver.observe(node);
  }
  function stopAnimations() {animations.forEach(animation => animation.cancel());animations.clear();}
  function resetDepth(node, state) {
    state.active=false;
    node.removeAttribute('data-re-depth-active');
    ['--re-tilt-x','--re-tilt-y','--re-shadow-x'].forEach(key => node.style.removeProperty(key));
  }
  function pointerEnabled() {return desktop.matches && !reduced.matches && !suspended && !document.hidden;}
  function schedulePointer() {if (!pointerFrame && pointerEnabled()) pointerFrame=requestAnimationFrame(updatePointers);}
  function updatePointers() {
    pointerFrame=0;
    if (!pointerEnabled()) return;
    depth.forEach((state,node) => {
      if (!state.active || !state.bounds) return;
      const x=Math.max(-1,Math.min(1,((state.x-state.bounds.left)/state.bounds.width-.5)*2));
      const y=Math.max(-1,Math.min(1,((state.y-state.bounds.top)/state.bounds.height-.5)*2));
      node.style.setProperty('--re-tilt-x', `${(-y*2.6).toFixed(2)}deg`);
      node.style.setProperty('--re-tilt-y', `${(x*3.4).toFixed(2)}deg`);
      node.style.setProperty('--re-shadow-x', `${(-x*6).toFixed(1)}px`);
      node.dataset.reDepthActive='true';
    });
    banners.forEach(state => {
      if (!state.active || !state.bounds) return;
      const image=state.root.querySelector('[data-re-slide].is-active .re-campaign-artwork img');
      if (!image) return;
      image.dataset.reHeroDepth='';
      const x=Math.max(-1,Math.min(1,((state.x-state.bounds.left)/state.bounds.width-.5)*2));
      const y=Math.max(-1,Math.min(1,((state.y-state.bounds.top)/state.bounds.height-.5)*2));
      image.style.setProperty('--re-hero-x', `${(x*5).toFixed(1)}px`);
      image.style.setProperty('--re-hero-y', `${(y*3).toFixed(1)}px`);
    });
  }
  function bindDepth(node) {
    if (depth.has(node)) return;
    node.dataset.reDepth='';
    const state={active:false,bounds:null,x:0,y:0};depth.set(node,state);
    const enter=e => {
      if (e.pointerType!=='mouse' || !pointerEnabled()) return;
      state.bounds=node.getBoundingClientRect();state.active=true;state.x=e.clientX;state.y=e.clientY;schedulePointer();
    };
    node.addEventListener('pointerenter',enter,{passive:true});
    node.addEventListener('pointermove',e => {
      if (e.pointerType!=='mouse' || !pointerEnabled()) return;
      if (!state.active) {enter(e);return;}
      state.x=e.clientX;state.y=e.clientY;schedulePointer();
    },{passive:true});
    node.addEventListener('pointerleave',()=>resetDepth(node,state),{passive:true});
    node.addEventListener('pointercancel',()=>resetDepth(node,state),{passive:true});
    node.addEventListener('focusin',()=>resetDepth(node,state));
    // Native drag, link click, keyboard focus and image zoom stay untouched.
  }
  function resetBanner(state) {
    state.active=false;
    state.root.querySelectorAll('[data-re-hero-depth]').forEach(image => {
      image.style.removeProperty('--re-hero-x');image.style.removeProperty('--re-hero-y');
    });
  }
  function heroEntry(slide) {
    if (!slide || mobile.matches || reduced.matches || suspended || document.hidden) return;
    const bounds=slide.getBoundingClientRect();
    if (bounds.bottom<=0 || bounds.top>=innerHeight) return;
    const pieces=[...slide.querySelectorAll('.re-campaign-copy > .re-eyebrow,.re-banner-title,.re-hero-lead,.re-hero-actions')];
    pieces.forEach((node,index) => animate(node,[{opacity:.15,transform:'translateY(14px)'},{opacity:1,transform:'translateY(0)'}],{duration:600,delay:index*65,fill:'backwards'}));
    slide.dataset.reHeroEntered='true';
  }
  function bindBanner(root) {
    if (banners.has(root)) return;
    const state={root,active:false,bounds:null,x:0,y:0};banners.set(root,state);
    root.addEventListener('pointerenter',e => {
      if(e.pointerType!=='mouse'||!pointerEnabled()) return;
      state.active=true;state.bounds=root.getBoundingClientRect();state.x=e.clientX;state.y=e.clientY;schedulePointer();
    },{passive:true});
    root.addEventListener('pointermove',e => {
      if(e.pointerType!=='mouse'||!pointerEnabled()) return;
      if(!state.active){state.active=true;state.bounds=root.getBoundingClientRect();}
      state.x=e.clientX;state.y=e.clientY;schedulePointer();
    },{passive:true});
    root.addEventListener('pointerleave',()=>resetBanner(state),{passive:true});
    root.addEventListener('pointercancel',()=>resetBanner(state),{passive:true});
    state.observer=new MutationObserver(records => {
      const slides=new Set(records.map(record=>record.target).filter(node=>node.matches('[data-re-slide].is-active')));
      slides.forEach(heroEntry);
    });
    state.observer.observe(root,{subtree:true,attributes:true,attributeFilter:['class']});
    heroEntry(root.querySelector('[data-re-slide].is-active'));
  }
  function scheduleScroll() {
    if (!scrollFrame && pointerEnabled() && visibleDrift.size) scrollFrame=requestAnimationFrame(updateScroll);
  }
  function updateScroll() {
    scrollFrame=0;
    if (!pointerEnabled()) return;
    // Batch all reads before writes. No work is scheduled while scrolling is idle.
    const positions=[...visibleDrift].map(node=>({node,bounds:node.parentElement.getBoundingClientRect()}));
    positions.forEach(({node,bounds})=>{
      const progress=Math.max(-1,Math.min(1,(innerHeight/2-(bounds.top+bounds.height/2))/innerHeight));
      node.style.setProperty('--re-drift-y',`${(progress*14).toFixed(1)}px`);
    });
  }
  const singles = [
    '.re-home-intro > *','.re-section-heading','.re-heritage-heading','.re-comparison-heading',
    '.re-r36s-video-heading','.re-ultra-story-intro','.re-r36s-moments-intro',
    '.re-px-story-media','.re-px-story-copy','.re-px-benefits header','.re-px-comparison > header',
    '.re-px-compare-box','.re-review-banner','.re-heritage > img',
    '.clock-heading','.clock-video-section__copy','.clock-video-section__frame','.faq-layout__heading',
    '.bx-about-panel','.sr-split > div','.bx-story-media','.bx-story-copy'
  ].join(',');
  const cards = '.re-model-tile,.ks-product,.re-details-grid > article,.re-home-service-item,.re-px-benefit-grid > article,.re-r36s-moment,.re-ultra-story-card';
  function scan(root=document) {
    root.querySelectorAll(`${singles},${cards}`).forEach(reveal);
    root.querySelectorAll('.re-model-tile,.ks-product,.re-px-story-media').forEach(bindDepth);
    root.querySelectorAll('.re-px-story-media > img').forEach(node => {
      if(drift.has(node))return;
      drift.add(node);node.dataset.reDrift='';driftObserver?.observe(node);
    });
    root.querySelectorAll('[data-re-banner]').forEach(bindBanner);
  }
  function resetPointers() {
    cancelAnimationFrame(pointerFrame);pointerFrame=0;
    cancelAnimationFrame(scrollFrame);scrollFrame=0;
    depth.forEach((state,node)=>resetDepth(node,state));
    banners.forEach(resetBanner);
    drift.forEach(node=>node.style.removeProperty('--re-drift-y'));
  }
  function onPreference() {
    resetPointers();
    if(reduced.matches || mobile.matches){
      stopAnimations();
      targets.forEach(node=>{seen.add(node);node.dataset.reReveal='shown';revealObserver?.unobserve(node);});
    }
    scheduleScroll();
  }
  makeObservers();scan();
  reduced.addEventListener('change',onPreference);
  desktop.addEventListener('change',onPreference);
  mobile.addEventListener('change',onPreference);
  window.addEventListener('scroll',()=>{resetActiveBounds();scheduleScroll();},{passive:true});
  function resetActiveBounds(){
    // Scrolling under a stationary pointer must not leave a tilted/offscreen card.
    depth.forEach((state,node)=>{if(state.active)resetDepth(node,state);});
    banners.forEach(state=>{if(state.active)resetBanner(state);});
  }
  window.addEventListener('resize',()=>{resetPointers();scheduleScroll();},{passive:true});
  document.addEventListener('visibilitychange',()=>{
    if(document.hidden){stopAnimations();resetPointers();}else scheduleScroll();
  });
  document.addEventListener('shopify:section:load',event=>scan(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    targets.forEach(node=>{if(event.target.contains(node)){revealObserver?.unobserve(node);animations.get(node)?.cancel();animations.delete(node);targets.delete(node);}});
    depth.forEach((_,node)=>{if(event.target.contains(node))depth.delete(node);});
    drift.forEach(node=>{if(event.target.contains(node)){driftObserver?.unobserve(node);visibleDrift.delete(node);drift.delete(node);}});
    banners.forEach((state,node)=>{if(event.target.contains(node)){state.observer.disconnect();banners.delete(node);}});
  });
  window.addEventListener('pagehide',()=>{suspended=true;stopAnimations();resetPointers();revealObserver?.disconnect();driftObserver?.disconnect();});
  window.addEventListener('pageshow',event=>{
    suspended=false;
    if(event.persisted){
      targets.forEach(node=>{if(!seen.has(node))revealObserver?.observe(node);});
      drift.forEach(node=>driftObserver?.observe(node));
      resetPointers();scheduleScroll();
    }
  });
})();

(() => {
 const timelines=[...document.querySelectorAll('.bx-delivery')];
 if(!timelines.length||!('IntersectionObserver'in window))return;
 const visible=new Set();
 const sync=()=>timelines.forEach(section=>{
  const active=visible.has(section)&&!document.hidden;
  section.toggleAttribute('data-re-delivery-visible',active);
  if(active){const track=section.querySelector('[data-delivery-timeline]');if(track) section.style.setProperty('--re-delivery-distance',`${track.clientWidth*.666}px`);}
 });
 const observer=new IntersectionObserver(entries=>{entries.forEach(({target,isIntersecting})=>isIntersecting?visible.add(target):visible.delete(target));sync();});
 timelines.forEach(section=>observer.observe(section));
 document.addEventListener('visibilitychange',sync);
 window.addEventListener('pagehide',()=>timelines.forEach(section=>section.removeAttribute('data-re-delivery-visible')));
 window.addEventListener('pageshow',sync);
})();

(() => {
 const button=document.getElementById('click-to-top');if(!button)return;
 let frame=0;
 const update=()=>{
  frame=0;button.hidden=scrollY<=100;
  if(!button.hidden){const height=document.documentElement.scrollHeight-innerHeight;button.style.setProperty('--re-scroll-progress',`${height>0?Math.min(100,scrollY/height*100):0}%`);}
 };
 const schedule=()=>{if(!frame)frame=requestAnimationFrame(update);};
 button.addEventListener('click',()=>window.scrollTo({top:0,behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'instant':'smooth'}));
 window.addEventListener('scroll',schedule,{passive:true});
 window.addEventListener('pageshow',schedule);
})();

(() => {
 const videos=[...document.querySelectorAll('video[data-re-poster]')];
 const load=video=>{video.poster=video.dataset.rePoster;delete video.dataset.rePoster;};
 if(!('IntersectionObserver'in window)){videos.forEach(load);return;}
 const observer=new IntersectionObserver(entries=>entries.forEach(({target,isIntersecting})=>{if(isIntersecting){load(target);observer.unobserve(target);}}),{rootMargin:'320px 0px'});
 videos.forEach(video=>observer.observe(video));
})();
